import React from 'react';
import {
  Card,
  Box,
  Typography,
  alpha,
  useTheme,
  Chip,
  IconButton,
  Tooltip,
  useMediaQuery
} from '@mui/material';
import {
  CalendarToday as CalendarIcon,
  Person as PersonIcon,
  Task as TaskIcon,
  MoreVert as MoreIcon,
  Business as BusinessIcon,
} from '@mui/icons-material';
import { format } from 'date-fns';
import { sv } from 'date-fns/locale';
import WorkStateIndicator from '../../work-state-indicator/WorkStateIndicator';
import ProbabilityIndicator from '../../probability-indicator/ProbabilityIndicator';
import { WorkOpportunity } from '../../../store/services/opportunityService';
import { useNavigate } from 'react-router-dom';

interface OpportunityItemProps {
  opportunity: WorkOpportunity;
  onMenuOpen: (event: React.MouseEvent<HTMLButtonElement>) => void;
  index: number; // För att alternera färger
  to?: string;
}

const OpportunityItem: React.FC<OpportunityItemProps> = ({ 
  opportunity, 
  onMenuOpen, 
  index,
  to = `/opportunities/${opportunity.OpportunityNo}`
}) => {
  const theme = useTheme();
  const navigate = useNavigate();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
  
  // Alternera färger baserat på index
  const colorOptions = [
    theme.palette.primary.main,
    theme.palette.secondary.main,
    theme.palette.success.main,
    theme.palette.info.main
  ];
  const color = colorOptions[index % colorOptions.length];
  
  const formatCurrency = (amount?: number | null) => {
    if (amount === undefined || amount === null) return '0 kr';
    return new Intl.NumberFormat('sv-SE', {
      style: 'currency',
      currency: 'SEK',
      maximumFractionDigits: 0
    }).format(amount);
  };

  const formatDate = (dateString?: string | null) => {
    if (!dateString) return '—';
    return format(new Date(dateString), 'd MMM yyyy', { locale: sv });
  };

  const handleClick = () => {
    navigate(to);
  };

  const handleMenuClick = (e: React.MouseEvent<HTMLButtonElement>) => {
    e.stopPropagation();
    onMenuOpen(e);
  };

  // Hämta stepnamn baserat på OpportunityStep
  const getStepName = (step: number) => {
    switch(step) {
      case 1: return 'Pre-sale';
      case 2: return 'Detailed-sale';
      default: return 'Okänd';
    }
  };

  return (
    <Card
      elevation={0}
      sx={{
        borderRadius: 1,
        border: `1px solid ${alpha(theme.palette.divider, 0.1)}`,
        borderLeft: `4px solid ${color}`,
        mb: 1,
        transition: 'all 0.2s ease',
        bgcolor: 'white',
        cursor: 'pointer',
        '&:hover': {
          boxShadow: `0 2px 8px ${alpha(theme.palette.common.black, 0.08)}`,
          borderLeftWidth: '6px',
        },
      }}
      onClick={handleClick}
    >
      {isMobile ? (
        // Mobil layout (tre rader)
        <Box sx={{ p: 1.5 }}>
          {/* Rad 1: Titel och pris */}
          <Box sx={{ 
            display: 'flex', 
            justifyContent: 'space-between',
            alignItems: 'center',
            mb: 1
          }}>
            <Typography 
              variant="subtitle1" 
              fontWeight={500} 
              sx={{ 
                maxWidth: '75%',
                overflow: 'hidden',
                textOverflow: 'ellipsis',
                whiteSpace: 'nowrap'
              }}
              title={opportunity.OpportunityName}
            >
              {opportunity.OpportunityName}
            </Typography>
            
            <Box sx={{ display: 'flex', alignItems: 'center' }}>
              <Typography 
                variant="subtitle1" 
                fontWeight={600} 
                color={theme.palette.success.main}
              >
                {formatCurrency(opportunity.OpportunityTotalAmount)}
              </Typography>
              
              <IconButton 
                size="small" 
                onClick={handleMenuClick}
                sx={{ ml: 0.5, p: 0.5 }}
              >
                <MoreIcon fontSize="small" />
              </IconButton>
            </Box>
          </Box>
          
          {/* Rad 2: Kund, ansvarig och datum */}
          <Box sx={{ 
            display: 'flex', 
            flexWrap: 'wrap',
            gap: 1,
            mb: 1
          }}>
            <Box sx={{ display: 'flex', alignItems: 'center', mr: 1 }}>
              <BusinessIcon 
                sx={{ 
                  fontSize: 14, 
                  color: theme.palette.text.secondary, 
                  mr: 0.5 
                }} 
              />
              <Typography 
                variant="body2" 
                color="textSecondary" 
                noWrap
                title={opportunity.CustomerName || 'Ingen kund'}
              >
                {opportunity.CustomerName || 'Ingen kund'}
              </Typography>
            </Box>
            
            <Box sx={{ display: 'flex', alignItems: 'center', mr: 1 }}>
              <PersonIcon 
                sx={{ 
                  fontSize: 14, 
                  color: theme.palette.text.secondary, 
                  mr: 0.5 
                }} 
              />
              <Typography 
                variant="body2" 
                color="textSecondary" 
                noWrap
                title={opportunity.AssignedToName || 'Inte tilldelad'}
              >
                {opportunity.AssignedToName || 'Inte tilldelad'}
              </Typography>
            </Box>
            
            <Box sx={{ display: 'flex', alignItems: 'center' }}>
              <CalendarIcon 
                sx={{ 
                  fontSize: 14, 
                  color: theme.palette.text.secondary, 
                  mr: 0.5 
                }} 
              />
              <Typography variant="body2" color="textSecondary">
                {formatDate(opportunity.OppertunityNextActionDate)}
              </Typography>
            </Box>
          </Box>
          
          {/* Rad 3: Statusar och indikatorer */}
          <Box sx={{ 
            display: 'flex', 
            flexWrap: 'wrap',
            alignItems: 'center',
            gap: 1
          }}>
            <ProbabilityIndicator value={opportunity.OpportunityProbability} compact={true} />
            <WorkStateIndicator state={opportunity.WorkState} />
            <Chip 
              label={getStepName(opportunity.OpportunityStep)}
              size="small"
              sx={{ 
                height: 20,
                '& .MuiChip-label': { px: 1, py: 0, fontSize: '0.7rem' },
                bgcolor: alpha(color, 0.8),
                color: 'white',
              }}
            />
            
            {opportunity.WorkItems && opportunity.WorkItems.length > 0 && (
              <Box sx={{ 
                display: 'flex', 
                alignItems: 'center',
              }}>
                <TaskIcon 
                  sx={{ 
                    fontSize: 14, 
                    color: theme.palette.text.secondary, 
                    mr: 0.5 
                  }} 
                />
                <Typography variant="caption" color="textSecondary">
                  {opportunity.WorkItems.length}
                </Typography>
              </Box>
            )}
          </Box>
        </Box>
      ) : (
        // Desktop layout (original, två rader)
        <>
          {/* Första raden */}
          <Box sx={{ 
            display: 'flex', 
            alignItems: 'center',
            px: 2,
            pt: 1.5,
            pb: 0.5
          }}>
            <Box sx={{ width: '32%', pr: 1 }}>
              <Typography 
                variant="subtitle1" 
                fontWeight={500} 
                noWrap 
                title={opportunity.OpportunityName}
              >
                {opportunity.OpportunityName}
              </Typography>
            </Box>
            
            <Box sx={{ width: '20%', display: 'flex', alignItems: 'center' }}>
              <Tooltip title="Kund">
                <BusinessIcon 
                  sx={{ 
                    fontSize: 14, 
                    color: theme.palette.text.secondary, 
                    mr: 0.5 
                  }} 
                />
              </Tooltip>
              <Typography 
                variant="body2" 
                color="textSecondary" 
                noWrap
                title={opportunity.CustomerName || 'Ingen kund'}
              >
                {opportunity.CustomerName || 'Ingen kund'}
              </Typography>
            </Box>
            
            <Box sx={{ width: '20%', display: 'flex', alignItems: 'center' }}>
              <Tooltip title="Ansvarig">
                <PersonIcon 
                  sx={{ 
                    fontSize: 14, 
                    color: theme.palette.text.secondary, 
                    mr: 0.5 
                  }} 
                />
              </Tooltip>
              <Typography 
                variant="body2" 
                color="textSecondary" 
                noWrap
                title={opportunity.AssignedToName || 'Inte tilldelad'}
              >
                {opportunity.AssignedToName || 'Inte tilldelad'}
              </Typography>
            </Box>
            
            <Box sx={{ width: '15%', display: 'flex', alignItems: 'center' }}>
              <Tooltip title="Slutdatum">
                <CalendarIcon 
                  sx={{ 
                    fontSize: 14, 
                    color: theme.palette.text.secondary, 
                    mr: 0.5 
                  }} 
                />
              </Tooltip>
              <Typography variant="body2" color="textSecondary">
                {formatDate(opportunity.OppertunityNextActionDate)}
              </Typography>
            </Box>
            
            <Box sx={{ width: '13%', textAlign: 'right' }}>
              <Typography 
                variant="subtitle1" 
                fontWeight={600} 
                color={theme.palette.success.main}
              >
                {formatCurrency(opportunity.OpportunityTotalAmount)}
              </Typography>
            </Box>
          </Box>
          
          {/* Andra raden */}
          <Box sx={{ 
            display: 'flex', 
            alignItems: 'center',
            px: 2,
            pt: 0.5,
            pb: 1.5,
            justifyContent: 'space-between'
          }}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              <ProbabilityIndicator value={opportunity.OpportunityProbability} compact={true} />
              <WorkStateIndicator state={opportunity.WorkState} />
              <Chip 
                label={getStepName(opportunity.OpportunityStep)}
                size="small"
                sx={{ 
                  height: 20,
                  '& .MuiChip-label': { px: 1, py: 0, fontSize: '0.7rem' },
                  bgcolor: alpha(color, 0.8),
                  color: 'white',
                }}
              />
              
              {opportunity.WorkItems && opportunity.WorkItems.length > 0 && (
                <Box sx={{ 
                  display: 'flex', 
                  alignItems: 'center',
                  ml: 1
                }}>
                  <Tooltip title="Aktiviteter">
                    <TaskIcon 
                      sx={{ 
                        fontSize: 14, 
                        color: theme.palette.text.secondary, 
                        mr: 0.5 
                      }} 
                    />
                  </Tooltip>
                  <Typography variant="caption" color="textSecondary">
                    {opportunity.WorkItems.length}
                  </Typography>
                </Box>
              )}
            </Box>
            
            <Box>
              <IconButton 
                size="small" 
                onClick={handleMenuClick}
                sx={{ ml: 0.5, p: 0.5 }}
              >
                <MoreIcon fontSize="small" />
              </IconButton>
            </Box>
          </Box>
        </>
      )}
    </Card>
  );
};

export default OpportunityItem;